import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {getAuth} from 'firebase/auth';
import {collection, doc, getDoc, getDocs, query, where} from 'firebase/firestore';
import {db, useAuthState} from '../../../shared/services/firebase';
import PropTypes from 'prop-types';
import {useTenants} from '../../tenants/context/TenantsContext';

const EdgesContext = createContext();

export const useEdges = () => useContext(EdgesContext);

export const EdgesProvider = ({children}) => {
  const authState = useAuthState();
  const {currentTenant} = useTenants();
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({active: 0, errors: 0});

  const fetchEdges = useCallback(async () => {
    if (!authState.user) {
      setEdges([]);
      setLoading(false);
      return;
    }

    if (!currentTenant) {
      setEdges([]);
      setLoading(false);
      return;
    }

    const auth = getAuth();
    if (!auth.currentUser) return;

    setLoading(true);
    try {
      let eData = [];

      const q = query(
        collection(db, 'edges'),
        where('tenantIds', 'array-contains', currentTenant.id)
      );

      const querySnapshot = await getDocs(q);

      const owners = currentTenant.owners || [];
      const admins = currentTenant.admins || [];
      const userEmail = authState.user.email;
      const isAdmin = [...owners, ...admins].some((m) => {
        if (typeof m === 'string') return m.trim() === userEmail.trim();
        if (m?.path) return m.path.endsWith(userEmail);
        return false;
      });

      await Promise.all(
        querySnapshot.docs.map(async (docSnap) => {
          try {
            const balenaUpdateSnap = await getDoc(doc(db, 'balenaUpdate', docSnap.id));
            eData.push({
              ...docSnap.data(),
              id: docSnap.id,
              isAdmin,
              visible: true,
              balenaUpdatePending: balenaUpdateSnap.exists(),
            });
          } catch (err) {
            console.error(`Error fetching edge ${docSnap.id}`, err);
          }
        })
      );

      const buildTenantPath = (tenantIds) =>
        tenantIds.reduce((path, id, i) => (i === 0 ? `tenants/${id}` : `${path}/tenants/${id}`), '');

      const uniqueImmediateTenantIds = [
        ...new Set(eData.map((e) => e.tenantIds?.at(-1)).filter((id) => id && id !== currentTenant.id)),
      ];

      const tenantNameCache = {};
      await Promise.all(
        uniqueImmediateTenantIds.map(async (tenantId) => {
          try {
            const edge = eData.find((e) => e.tenantIds?.at(-1) === tenantId);
            if (!edge?.tenantIds) return;
            const path = buildTenantPath(edge.tenantIds);
            const tenantSnap = await getDoc(doc(db, path));
            if (tenantSnap.exists()) {
              tenantNameCache[tenantId] = tenantSnap.data().name;
            }
          } catch (err) {
            console.warn(`Could not resolve tenant name for ${tenantId}`, err);
          }
        })
      );

      eData = eData.map((e) => {
        const immediateId = e.tenantIds?.at(-1);
        const immediateTenantName =
          immediateId === currentTenant.id ? currentTenant.name : (tenantNameCache[immediateId] ?? null);
        return {...e, immediateTenantName};
      });

      eData = eData.sort((a, b) => {
        if (a.id < b.id) return -1;
        if (a.id > b.id) return 1;
        return 0;
      });

      setEdges(eData);

      let activeCount = 0;
      let errorCount = 0;
      eData.forEach((edge) => {
        const deadman = edge.currentStates?.deadmanCheck;
        if (deadman?.type === 401) {
          errorCount++;
        } else {
          activeCount++;
        }
      });
      setStats({active: activeCount, errors: errorCount});
    } catch (error) {
      console.error('Error fetching edges:', error);
    } finally {
      setLoading(false);
    }
  }, [authState.user, currentTenant]);

  useEffect(() => {
    fetchEdges();
  }, [fetchEdges]);

  const updateEdgeState = useCallback((id, newData) => {
    setEdges((prev) => prev.map((e) => (e.id === id ? {...e, ...newData} : e)));
  }, []);

  const value = useMemo(
    () => ({edges, loading, stats, refresh: fetchEdges, updateEdgeState}),
    [edges, loading, stats, fetchEdges, updateEdgeState]
  );

  return <EdgesContext.Provider value={value}>{children}</EdgesContext.Provider>;
};

EdgesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
